// React → legacy shell helper (M1 Phase 4, PR 9).
//
// The legacy desktop `createModule` no longer builds the `.placed-module` DOM —
// it dispatches a new module into the store and React's `<Workspace>` renders the
// `<PlacedModule>`. But several legacy callers use the returned DOM node
// immediately (drag-drop + tour add a `snap-judder` class to it). React renders
// asynchronously, so `flushSync` forces `<Workspace>` to render the new node
// synchronously; we then hand back the freshly-rendered element.
//
// Also carries the two pure control-config lookups `buildModuleData` needs to
// seed a new module's per-title default controls (PR 11 — moved here when the
// `bentoControls` mount bridge was deleted; its `.render()`/`.unmount()` API had
// no live callers once `<PlacedModule>` started composing `<ModuleControls>`
// directly, but these two lookups are still called before anything mounts).
//
// Exposed on `window` (see main.tsx) because the caller is a classic <script>
// that can't import ES modules. Removed once `createModule` itself is React.

import { flushSync } from "react-dom";
import { useBentoStore } from "./store/index.js";
import type { BentoModule, ModuleControls } from "./store/module.js";
import {
  hasReactControls,
  initialControlsFor,
} from "./components/moduleControlsConfig.js";

export const bentoMount = {
  /** Add a module to the store (push) and return its freshly-rendered DOM node. */
  addModule(data: BentoModule): HTMLElement | null {
    flushSync(() => useBentoStore.getState().addModule(data));
    return document.querySelector<HTMLElement>(`[data-module-id="${data.id}"]`);
  },
  /** Insert a module at an array index (mobile stack order, PR 10) + return the
   *  freshly-rendered node. `Infinity` appends. */
  addModuleAt(data: BentoModule, index: number): HTMLElement | null {
    flushSync(() => useBentoStore.getState().addModuleAt(data, index));
    return document.querySelector<HTMLElement>(`[data-module-id="${data.id}"]`);
  },
  /** Does this module title get React-rendered sub-controls? */
  hasReactControls(title: string): boolean {
    return hasReactControls(title);
  },
  /** The per-title default control values `buildModuleData` should seed. */
  initialControlsFor(title: string): Partial<ModuleControls> {
    return initialControlsFor(title);
  },
};

export type BentoMount = typeof bentoMount;
